import React, { useEffect, useState } from 'react';
import Sidebar from "./sidebar";
import { useParams } from "react-router-dom";
import { Container } from 'react-bootstrap';
import Dashboardheader from "./dashboardheader";
import { getSchedulesAction, getEmployeeByIdAction } from '../../Action/modernAdminActions';
import toast from 'react-hot-toast';
// Load FullCalendar and plugins on demand to reduce initial bundle size
let FullCalendarCmp = null;
let dayGridPlugin = null;
let timeGridPlugin = null;
let interactionPlugin = null;

// Function to transform modern Schedule model data into FullCalendar events
const transformScheduleDataToEvents = (schedules) => {
  if (!Array.isArray(schedules)) return [];
  
  return schedules.map((schedule) => {
    // Extract time from datetime strings for display
    const startTime = new Date(schedule.start_time).toTimeString().substring(0, 5);
    const endTime = new Date(schedule.end_time).toTimeString().substring(0, 5);
    
    return {
      id: schedule.schedule_id || schedule.id,
      start: schedule.start_time,
      end: schedule.end_time,
      title: `${schedule.shift_type || 'Duty'}: ${startTime} - ${endTime}`,
      backgroundColor: getStatusColor(schedule.status),
      extendedProps: {
        scheduleId: schedule.schedule_id,
        employeeId: schedule.employee_id,
        shiftType: schedule.shift_type,
        status: schedule.status,
        location: schedule.location,
        notes: schedule.notes,
        fromTime: startTime,
        toTime: endTime
      }
    };
  });
};

// Helper function to get color based on schedule status
const getStatusColor = (status) => {
  switch (status) {
    case 'scheduled': return '#2563eb';
    case 'in_progress': return '#16a34a';
    case 'completed': return '#65a30d';
    case 'cancelled': return '#dc2626';
    default: return '#6b7280';
  }
};

const CalendarComponent = () => {
  let { id } = useParams();
  const [schedules, setSchedules] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [show, setShow] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [eventDetails, setEventDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [calendarReady, setCalendarReady] = useState(false);

  // Transform the schedule data into events for the calendar
  const events = transformScheduleDataToEvents(schedules);
  
  useEffect(() => {
    let mounted = true;
    // dynamically import calendar modules
    (async () => {
      try {
        const [fc, dayGrid, timeGrid, interact] = await Promise.all([
          import('@fullcalendar/react'),
          import('@fullcalendar/daygrid'),
          import('@fullcalendar/timegrid'),
          import('@fullcalendar/interaction')
        ]);
        if (mounted) {
          FullCalendarCmp = fc.default;
          dayGridPlugin = dayGrid.default;
          timeGridPlugin = timeGrid.default;
          interactionPlugin = interact.default;
          setCalendarReady(true);
        }
      } catch (e) {
        console.error('Failed to load calendar modules', e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    loadEmployeeSchedules();
  }, [id]);

  const loadEmployeeSchedules = async () => {
    try {
      setIsLoading(true);
      
      // Load employee details first using the staff details action
      const employeeRes = await getEmployeeByIdAction(id);
      if (employeeRes.success) {
        setEmployee(employeeRes.data);
        
        // Load schedules for this employee using the staff ID directly
        const schedulesRes = await getSchedulesAction({
          employee_id: id, // Use the staff ID directly from URL params
          // Get schedules for the next 3 months
          start_date: new Date().toISOString().split('T')[0],
          end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        });
        
        if (schedulesRes.success && schedulesRes.data) {
          const scheduleData = Array.isArray(schedulesRes.data) ? schedulesRes.data : [];
          setSchedules(scheduleData);
          
          if (scheduleData.length === 0) {
            console.log("No schedules found for employee ID:", id);
          }
        } else {
          setSchedules([]);
          console.warn("No schedules found for employee:", schedulesRes.message);
        }
      } else {
        // Try to load schedules anyway with just the ID from URL
        console.log("Employee details failed, attempting to load schedules with ID:", id);
        const schedulesRes = await getSchedulesAction({
          employee_id: id,
          start_date: new Date().toISOString().split('T')[0],
          end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        });
        
        if (schedulesRes.success && schedulesRes.data) {
          setSchedules(Array.isArray(schedulesRes.data) ? schedulesRes.data : []);
        } else {
          setSchedules([]);
        }
        
        toast.error(employeeRes.message || "Failed to load employee details");
      }
      
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setSchedules([]);
      console.error('Error loading employee schedules:', error);
      toast.error("An error occurred while loading the schedule");
    }
  };

  const handleEventClick = (clickInfo) => {
    setEventDetails(clickInfo.event);
    setShow(true);
  };

  const determineBackgroundColor = (eventDate) => {
    const today = new Date();
    if (eventDate < today) {
      return { backgroundColor: 'rgb(234, 67, 53)', color: '#ffff', borderRadius: '3px', padding: '4px' };
    } else if (eventDate.toDateString() === today.toDateString()) {
      return { backgroundColor: 'rgb(11, 128, 67)', color: '#ffff', borderRadius: '3px', padding: '4px' };
    } else {
      return { backgroundColor: 'rgb(11, 128, 67)', color: '#ffff', borderRadius: '3px', padding: '4px' };
    }
  };

  const renderEventContent = (eventInfo) => {
    const backgroundColor = determineBackgroundColor(new Date(eventInfo.event.start));
    return (
      <div style={backgroundColor}>
        <i>{eventInfo.event.extendedProps.fromTime} - {eventInfo.event.extendedProps.toTime}</i>
      </div>
    );
  };

  if (isLoading) {
    return (
      <>
        <Dashboardheader />
        <div className="wrapper">
          <Sidebar />
          <div id="content" className="calendar">
            <Container fluid className="p-4">
              <h1>Employee Schedule{employee ? ` - ${employee.position || employee.user_id}` : ''}</h1>
              <div style={{ textAlign: "center", padding: "50px" }}>
                <img
                  style={{ width: "40px" }}
                  src="/assets/images/loading.gif"
                  alt="Loading..."
                />
                <p>Loading schedule...</p>
              </div>
            </Container>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Dashboardheader />
      <div className="wrapper">
        <Sidebar />
        <div id="content" className="calendar">
          <Container fluid className="p-4">
            {/* Header Section */}
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h4 className="mb-1" style={{ color: '#1f2937', fontWeight: '600' }}>
                    Staff Duty Calendar
                    {employee && (
                      <span style={{ color: '#6b7280', fontWeight: '400' }}>
                        {' - '}{employee.full_name || employee.first_name + ' ' + employee.last_name}
                      </span>
                    )}
                  </h4>
                  <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    View and manage individual staff schedules
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div style={{
                    backgroundColor: '#f0fdf4',
                    color: '#16a34a',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}>
                    {schedules.length} scheduled duties
                  </div>
                </div>
              </div>
            </div>

            {/* Calendar Container */}
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '20px'
            }}>
              <div className="calendar-main">
                {calendarReady ? (
                <FullCalendarCmp
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                  }}
                  selectable={true}
                  selectMirror={true}
                  dayMaxEvents={true}
                  eventClick={handleEventClick}
                  initialView="dayGridMonth"
                  events={events}
                  eventContent={renderEventContent}
                  height="auto"
                  aspectRatio={1.35}
                  eventDisplay="block"
                  dayMaxEventRows={3}
                />) : (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <p>Loading calendar...</p>
                  </div>
                )}
              </div>
              
              {/* No Schedule Message */}
              {events.length === 0 && !isLoading && (
                <div style={{ 
                  textAlign: "center", 
                  padding: "40px 20px", 
                  color: "#6b7280",
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '2px dashed #d1d5db',
                  marginTop: '20px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
                  <h6 style={{ color: '#374151', marginBottom: '8px' }}>No Schedule Found</h6>
                  <p style={{ margin: '0', fontSize: '0.875rem' }}>
                    This staff member doesn't have any scheduled duties yet.
                  </p>
                </div>
              )}
            </div>

            {/* Legend */}
            {events.length > 0 && (
              <div style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '16px',
                marginTop: '20px'
              }}>
                <h6 style={{ color: '#374151', marginBottom: '12px', fontSize: '0.875rem' }}>Schedule Legend</h6>
                <div className="d-flex gap-4 flex-wrap">
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#2563eb', borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Scheduled</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#16a34a', borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>In Progress</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#65a30d', borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Completed</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ width: '12px', height: '12px', backgroundColor: '#dc2626', borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Cancelled</span>
                  </div>
                </div>
              </div>
            )}
          </Container>
        </div>
      </div>
    </>
  );
};

export default CalendarComponent;
